import { useEffect, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, useCollectionBinding } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const ctaLinkDef: SlotGroupDefinition = {
  label: 'View Availability & Pricing CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Label', type: 'text', defaultValue: 'View Availability & Pricing' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

interface OverviewPhoto {
  image?: string;
  alt_text?: string;
  title?: string;
  original_url?: string;
}

const SLIDE_INTERVAL = 5000;

/**
 * Auto-rotating slideshow for the overview image column. Photos come from the
 * property's `photo_gallery` via a collection binding filtered to Apartment
 * titles, so the editor can reorder / hide / add slides. Auto-advance pauses
 * on hover/focus and is disabled entirely when the user prefers reduced motion.
 */
function OverviewSlideshow({ photos }: { photos: OverviewPhoto[] }) {
  const slides = photos
    .map((p) => ({ src: p.image || p.original_url || '', alt: p.alt_text || p.title || '' }))
    .filter((s) => s.src);
  const count = slides.length;

  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  const safeCurrent = count > 0 ? Math.min(current, count - 1) : 0;

  useEffect(() => {
    if (count < 2 || paused) return;
    if (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }
    const id = setInterval(() => setCurrent((p) => (p + 1) % count), SLIDE_INTERVAL);
    return () => clearInterval(id);
  }, [count, paused]);

  if (count === 0) {
    return <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />;
  }

  return (
    <div
      className="absolute inset-0"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      {slides.map((slide, i) => (
        <VibesitesImage
          key={slide.src}
          url={slide.src}
          alt={slide.alt}
          sizes="(min-width: 768px) 32rem, (min-width: 640px) 28rem, 24rem"
          className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700 ease-in-out ${
            i === safeCurrent ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ))}
    </div>
  );
}

const DESC_CLAMP_LENGTH = 200;

export function PropertyOverview() {
  const ctaLink = useSlotGroup('propertyoverview.cta_link', ctaLinkDef);
  const description = useContentToken('propertyoverview.description');
  const photos = useCollectionBinding<OverviewPhoto>('propertyoverview.images', {
    collection: 'photo_gallery',
    defaultLimit: 8,
    filter: { title: 'Apartment' },
  });

  const [descExpanded, setDescExpanded] = useState(false);
  const isLongDescription = !!description && String(description).length > DESC_CLAMP_LENGTH;

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const descriptionRef = useScrollReveal<HTMLDivElement>();
  const imageRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="pt-12 md:pt-20 pb-12 md:pb-20 bg-[var(--vibe-color-background)]">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-10 md:gap-14 items-center">
          {/* Left column — title / blurb */}
          <div className="text-center md:text-left md:order-1 order-2">
            <div ref={headingRef} className="reveal-zoom-front">
              <RichTextContent
                path="propertyoverview.pre_title"
                as="p"
                className="font-body text-sm sm:text-base font-semibold text-[var(--vibe-color-primary)] mb-5 tracking-[0.3em] uppercase"
              />

              <RichTextContent
                path="propertyoverview.title"
                as="h2"
                className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-[var(--vibe-color-text)] mb-8 leading-[1.05]"
              />
            </div>

            <div ref={descriptionRef} className="reveal-fade-up">
              <div
                className={`overview-desc-wrap ${
                  descExpanded || !isLongDescription ? 'overview-desc-wrap--open' : ''
                }`}
              >
                <RichTextContent
                  path="propertyoverview.description"
                  as="div"
                  className="font-body text-base sm:text-lg text-[var(--vibe-color-text)] max-w-xl mx-auto md:mx-0 leading-relaxed [&_a]:text-primary [&_a]:underline"
                />
              </div>
              {isLongDescription && (
                <button
                  type="button"
                  onClick={() => setDescExpanded((v) => !v)}
                  aria-expanded={descExpanded}
                  className="mt-3 font-body text-sm font-semibold tracking-wide text-[var(--vibe-color-primary)] hover:underline transition-colors"
                >
                  {descExpanded ? (
                    <RichTextContent path="propertyoverview.read_less_label" as="span" />
                  ) : (
                    <RichTextContent path="propertyoverview.read_more_label" as="span" />
                  )}
                </button>
              )}
            </div>

            <EditableSlotGroup groupPath="propertyoverview.cta_link" definition={ctaLinkDef}>
              <a
                href={ctaLink.url || '/floor-plans'}
                target={ctaLink._target || '_self'}
                className="overview-cta-btn group/cta relative inline-flex items-center justify-center gap-2 mt-9 px-4 py-2.5 lg:px-6 lg:py-3 font-body text-base font-normal normal-case whitespace-nowrap overflow-hidden transition-all duration-300 max-md:w-56"
              >
                <span className="relative z-10">
                  {ctaLink.label || 'View Availability & Pricing'}
                </span>
              </a>
            </EditableSlotGroup>
          </div>

          {/* Right column — slideshow */}
          <div ref={imageRef} className="reveal-slide-right md:order-2 order-1">
            <div className="relative block w-full max-w-sm sm:max-w-md md:max-w-none mx-auto md:mr-auto md:ml-0 aspect-[4/5] overflow-hidden rounded-md shadow-lg">
              <OverviewSlideshow photos={photos} />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .overview-desc-wrap {
          max-height: 9.6em;
          overflow: hidden;
          transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          -webkit-mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
          mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
        }
        .overview-desc-wrap--open {
          max-height: 60em;
          -webkit-mask-image: none;
          mask-image: none;
        }

        /* Match Header › schedule_tour_link (.nav-cta-btn): solid primary
           fill, 5px radius, surface fill + lift on hover. */
        .overview-cta-btn {
          background: var(--vibe-color-primary);
          border: 0;
          border-radius: 5px;
          color: #ffffff;
          box-shadow: none;
          transition: background-color 0.25s ease, color 0.25s ease,
            box-shadow 0.25s ease, transform 0.25s ease;
        }
        .overview-cta-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: transparent;
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .overview-cta-btn:hover {
          background: var(--vibe-color-surface);
          color: #ffffff;
          box-shadow:
            0 2px 6px rgba(0,0,0,0.1),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
          transform: translateY(-1px);
        }
        .overview-cta-btn:hover::before {
          opacity: 0;
        }
        .overview-cta-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.pre_title': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: 'Life at our community is like nowhere else',
    },
    'propertyoverview.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<span data-type="field-token" data-field="property_name"></span>',
    },
    'propertyoverview.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_long_description',
      defaultValue: '<p>Discover the perfect blend of comfort, style, and convenience at our community.</p>',
    },
    'propertyoverview.read_more_label': {
      label: 'Read More Label',
      type: 'richtext',
      defaultValue: '<p>Read More</p>',
    },
    'propertyoverview.read_less_label': {
      label: 'Read Less Label',
      type: 'richtext',
      defaultValue: '<p>Read Less</p>',
    },
  },
  slotGroups: {
    'propertyoverview.cta_link': ctaLinkDef,
  },
  collectionBindings: {
    'propertyoverview.images': {
      label: 'Slideshow Images',
      collection: 'photo_gallery',
      defaultLimit: 8,
      filter: { title: 'Apartment' },
      description:
        'Apartment-classified photos from the property gallery. These rotate in the overview slideshow.',
    },
  },
};

(PropertyOverview as any).contentSchema = contentSchema;

export default PropertyOverview;
